import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "../utils/motion";
import ScrollAnimation from "../components/ScrollAnimation";
import { ABOUT } from "../constants/copy";

/* ================== ENV ================== */
const GITHUB_USERNAME = process.env.REACT_APP_GITHUB_USERNAME;
const REPO_NAME = process.env.REACT_APP_GITHUB_REPO;
const BRANCH = process.env.REACT_APP_GITHUB_BRANCH;
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;

/* ================== AXIOS ================== */
const githubApi = axios.create({
  baseURL: "https://api.github.com",
  headers: {
    Authorization: `Bearer ${GITHUB_TOKEN}`,
    Accept: "application/vnd.github+json",
  },
});

const raw = (path) =>
  `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/${path}`;

/* ================== SKELETON ================== */
const Skeleton = () => (
  <div className="w-full h-full rounded-2xl bg-white/10 animate-pulse" />
);

export default function About() {
  const [introImage, setIntroImage] = useState(null);
  const [workImages, setWorkImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAboutImages = async () => {
      try {
        /* 🚀 cache */
        const cached = sessionStorage.getItem("about_images");
        if (cached) {
          const data = JSON.parse(cached);
          setIntroImage(data.intro);
          setWorkImages(data.work);
          setLoading(false);
          return;
        }

        const [introRes, workRes] = await Promise.all([
          githubApi.get(
            `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/about/hero`
          ),
          githubApi.get(
            `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/about/work`
          ),
        ]);

        const intro =
          introRes.data.find(i => i.type === "file")?.name || null;

        const work = workRes.data
          .filter(i => i.type === "file")
          .map(i => ({
            src: raw(`about/work/${i.name}`),
            alt: i.name.replace(/[-_]/g, " ").split(".")[0],
          }));

        const data = {
          intro: intro ? raw(`about/hero/${intro}`) : null,
          work,
        };

        setIntroImage(data.intro);
        setWorkImages(data.work);
        sessionStorage.setItem("about_images", JSON.stringify(data));
      } catch (err) {
        console.error("Failed to load about images", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutImages();
  }, []);

  const image3DVariants = {
    rest: { scale: 1, rotateY: 0 },
    hover: {
      scale: 1.05,
      rotateY: 5,
      transition: { duration: 0.35, ease: "easeOut" },
    },
  };

  return (
    <section className="px-4 sm:px-6 md:px-10 lg:px-16 xl:px-20 py-10 sm:py-14 md:py-20 lg:py-24">
      <div className="max-w-6xl mx-auto">

        {/* ================= HEADER ================= */}
        
          <h1 className="section-heading mb-4 sm:mb-6">
            {ABOUT.title}
          </h1>
          <p className="section-sub mb-10 sm:mb-14 md:mb-16">
            {ABOUT.main}
          </p>
       

        {/* ================= INTRO ================= */}
       
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start mb-14 sm:mb-16 md:mb-20">
            <div
              className="w-full max-w-lg overflow-hidden rounded-2xl shadow-card"
              variants={image3DVariants}
              initial="rest"
              whileHover="hover"
              style={{ perspective: 1000 }}
            >
              { (
                introImage && (
                  <img
                    src={introImage}
                    alt="Weddmac about"
                    loading="eager"
                    className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                )
              )}
            </div>

            <div className="space-y-5 sm:space-y-6">
              <p className="font-bodoni text-white/95 text-base sm:text-lg md:text-xl leading-relaxed italic">
                “{ABOUT.quote}”
              </p>
              <p className="font-inter text-white/80 text-sm sm:text-base md:text-lg leading-relaxed">
                {ABOUT.sub}
              </p>
              <Link to="/get-in-touch" className="btn-primary">
                Get In Touch
              </Link>
            </div>
          </div>
      

        {/* ================= OUR WORK ================= */}
       
          <h2 className="font-inria font-light text-xl sm:text-2xl md:text-3xl text-weddmac-gold/90 mb-5 sm:mb-6 md:mb-8 tracking-wide">
            Our Work
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[200px] md:auto-rows-[240px] gap-3 md:gap-4">
            {(loading ? Array.from({ length: 8 }) : workImages.slice(0, 8)).map(
              (item, i) => {
                const layout =
                  i === 0
                    ? "md:col-span-2 md:row-span-2"
                    : i === 3
                    ? "md:row-span-2"
                    : i === 6
                    ? "md:col-span-2"
                    : "";

                return (
                  <div
                    key={i}
                    className={`group relative overflow-hidden rounded-2xl shadow-card ${layout}`}
                    variants={image3DVariants}
                    initial="rest"
                    whileHover="hover"
                    style={{ perspective: 1000 }}
                  >
                    {loading ? (
                      <Skeleton />
                    ) : (
                      <img
                        src={item.src}
                        alt={item.alt}
                        loading="eager"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    )}

                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                );
              }
            )}
          </div>
      

      </div>
    </section>
  );
}
